import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useImportedAccounts, useAccountConfigurations } from './useAccountConfiguration';

export interface ReviewSummary {
  totalAccounts: number;
  accountsWithCollection: number;
  accountsWithParsing: number;
  accountsWithGLCode: number;
  accountsWithPayment: number;
  collectionSources: {
    portal: number;
    email: number;
    manual: number;
    api: number;
  };
  parsingMethods: {
    vendor_api: number;
    ai: number;
    manual: number;
  };
  readinessScore: number;
  dataCompletenessScore: number;
}

export interface ChecklistItem {
  id: string;
  category: 'data' | 'configuration' | 'quality' | 'testing';
  title: string;
  description: string;
  status: 'complete' | 'pending' | 'warning';
  required: boolean;
  details?: string;
  count?: number;
  total?: number;
}

export function useReviewData(projectId: string | undefined) {
  const { data: accounts = [], isLoading: accountsLoading } = useImportedAccounts(projectId);
  const { data: configurations = [], isLoading: configsLoading } = useAccountConfigurations(projectId);

  // Fetch staged data for quality metrics
  const { data: stagedData, isLoading: stagedLoading } = useQuery({
    queryKey: ['review-staged-data', projectId],
    queryFn: async () => {
      if (!projectId) return [];

      const { data: batches } = await supabase
        .from('tum_onboarding_import_batches')
        .select('id, batch_type, status, rows_success, rows_failed, rows_warning, total_rows')
        .eq('project_id', projectId);

      return batches || [];
    },
    enabled: !!projectId,
  });

  // Fetch insights
  const { data: insights, isLoading: insightsLoading } = useQuery({
    queryKey: ['review-insights', projectId],
    queryFn: async () => {
      if (!projectId) return null;

      const { data } = await (supabase as any)
        .from('tum_onboarding_insights')
        .select('*')
        .eq('project_id', projectId)
        .order('calculated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      return data;
    },
    enabled: !!projectId,
  });

  // Calculate summary - using correct property names from AccountConfiguration type
  const summary: ReviewSummary = {
    totalAccounts: accounts.length,
    accountsWithCollection: configurations.filter(c => c.collection_source || c.collection_completed).length,
    accountsWithParsing: configurations.filter(c => c.parsing_completed || (c.parsing_fields && c.parsing_fields.length > 0)).length,
    accountsWithGLCode: configurations.filter(c => c.gl_code_config && Object.keys(c.gl_code_config).length > 0).length,
    accountsWithPayment: configurations.filter(c => c.payment_config && Object.keys(c.payment_config).length > 0).length,
    collectionSources: {
      portal: configurations.filter(c => c.collection_source === 'portal').length,
      email: configurations.filter(c => c.collection_source === 'email').length,
      manual: configurations.filter(c => c.collection_source === 'manual').length,
      api: 0, // API not in current type - could extend later
    },
    parsingMethods: {
      vendor_api: configurations.filter(c => c.parsing_config && (c.parsing_config as any).method === 'vendor_api').length,
      ai: configurations.filter(c => c.parsing_config && (c.parsing_config as any).method === 'ai').length,
      manual: configurations.filter(c => c.parsing_config && (c.parsing_config as any).method === 'manual').length,
    },
    readinessScore: calculateReadinessScore(accounts.length, configurations),
    dataCompletenessScore: calculateDataCompleteness(stagedData || []),
  };

  // Generate dynamic checklist
  const checklist: ChecklistItem[] = generateChecklist(accounts, configurations, stagedData || [], insights, summary);

  const isLoading = accountsLoading || configsLoading || stagedLoading || insightsLoading;

  return {
    summary,
    checklist,
    accounts,
    configurations,
    insights,
    stagedData,
    isLoading,
  };
}

function calculateReadinessScore(
  totalAccounts: number,
  configurations: any[]
): number {
  if (totalAccounts === 0) return 0;

  const weights = {
    collection: 40, // 40% weight for collection source
    parsing: 30,    // 30% weight for parsing config
    glCode: 15,     // 15% weight for GL codes
    payment: 15,    // 15% weight for payment terms
  };

  const collectionScore = (configurations.filter(c => c.collection_source || c.collection_completed).length / totalAccounts) * weights.collection;
  const parsingScore = (configurations.filter(c => c.parsing_completed || (c.parsing_fields && c.parsing_fields.length > 0)).length / totalAccounts) * weights.parsing;
  const glScore = (configurations.filter(c => c.gl_code_config && Object.keys(c.gl_code_config).length > 0).length / totalAccounts) * weights.glCode;
  const paymentScore = (configurations.filter(c => c.payment_config && Object.keys(c.payment_config).length > 0).length / totalAccounts) * weights.payment;

  return Math.min(100, Math.round(collectionScore + parsingScore + glScore + paymentScore));
}

function calculateDataCompleteness(batches: any[]): number {
  if (batches.length === 0) return 0;

  const totalRows = batches.reduce((sum, b) => sum + (b.total_rows || 0), 0);
  const successRows = batches.reduce((sum, b) => sum + (b.rows_success || 0), 0);

  if (totalRows === 0) return 0;
  return Math.round((successRows / totalRows) * 100);
}

function generateChecklist(
  accounts: any[],
  configurations: any[],
  batches: any[],
  insights: any,
  summary: ReviewSummary
): ChecklistItem[] {
  const items: ChecklistItem[] = [];
  const totalAccounts = accounts.length;

  // Data Import Checklist Items
  const accountsBatch = batches.find(b => b.batch_type === 'accounts');
  const hasCompletedImport = accountsBatch?.status === 'completed';
  items.push({
    id: 'data_import',
    category: 'data',
    title: 'Data Import Complete',
    description: 'All required entities (properties, units, accounts) have been imported',
    status: hasCompletedImport ? 'complete' : 'pending',
    required: true,
    details: accountsBatch ? `${accountsBatch.rows_success || 0} accounts imported successfully` : 'No accounts imported',
    count: accountsBatch?.rows_success || 0,
    total: accountsBatch?.total_rows || 0,
  });

  // Data Quality Check
  const dataQuality = summary.dataCompletenessScore >= 90 ? 'complete' : summary.dataCompletenessScore >= 70 ? 'warning' : 'pending';
  items.push({
    id: 'data_quality',
    category: 'data',
    title: 'Data Quality Validation',
    description: 'All critical data quality issues have been resolved',
    status: dataQuality,
    required: true,
    details: `Data completeness: ${summary.dataCompletenessScore}%`,
  });

  // Collection Source Configuration
  const collectionPct = totalAccounts > 0 ? (summary.accountsWithCollection / totalAccounts) * 100 : 0;
  const collectionStatus = collectionPct >= 100 ? 'complete' : collectionPct >= 80 ? 'warning' : 'pending';
  items.push({
    id: 'collection_configured',
    category: 'configuration',
    title: 'Bill Collection Sources Configured',
    description: 'All accounts have collection sources (Portal, Email, or Manual) assigned',
    status: collectionStatus,
    required: true,
    details: `${summary.accountsWithCollection} of ${totalAccounts} accounts configured`,
    count: summary.accountsWithCollection,
    total: totalAccounts,
  });

  // Parsing Configuration
  const parsingPct = totalAccounts > 0 ? (summary.accountsWithParsing / totalAccounts) * 100 : 0;
  const parsingStatus = parsingPct >= 100 ? 'complete' : parsingPct >= 80 ? 'warning' : 'pending';
  items.push({
    id: 'parsing_configured',
    category: 'configuration',
    title: 'Bill Parsing Configuration',
    description: 'Parsing method configured for all accounts (Vendor API, AI, or Manual)',
    status: parsingStatus,
    required: true,
    details: `${summary.accountsWithParsing} of ${totalAccounts} accounts configured`,
    count: summary.accountsWithParsing,
    total: totalAccounts,
  });

  // GL Code Mappings (Optional but recommended)
  const glPct = totalAccounts > 0 ? (summary.accountsWithGLCode / totalAccounts) * 100 : 0;
  const glStatus = glPct >= 80 ? 'complete' : glPct >= 50 ? 'warning' : 'pending';
  items.push({
    id: 'gl_mappings',
    category: 'configuration',
    title: 'GL Code Mappings',
    description: 'GL codes assigned for accounting integration',
    status: glStatus,
    required: false,
    details: `${summary.accountsWithGLCode} of ${totalAccounts} accounts have GL codes`,
    count: summary.accountsWithGLCode,
    total: totalAccounts,
  });

  // Payment Terms (Optional)
  const paymentPct = totalAccounts > 0 ? (summary.accountsWithPayment / totalAccounts) * 100 : 0;
  const paymentStatus = paymentPct >= 80 ? 'complete' : paymentPct >= 50 ? 'warning' : 'pending';
  items.push({
    id: 'payment_terms',
    category: 'configuration',
    title: 'Payment Terms Configured',
    description: 'Payment terms and approval workflows defined',
    status: paymentStatus,
    required: false,
    details: `${summary.accountsWithPayment} of ${totalAccounts} accounts configured`,
    count: summary.accountsWithPayment,
    total: totalAccounts,
  });

  // Readiness Score
  const readinessStatus = summary.readinessScore >= 85 ? 'complete' : summary.readinessScore >= 70 ? 'warning' : 'pending';
  items.push({
    id: 'readiness_score',
    category: 'quality',
    title: 'System Readiness Score',
    description: 'Overall system readiness meets minimum threshold (85%)',
    status: readinessStatus,
    required: true,
    details: `Current readiness: ${summary.readinessScore}%`,
  });

  // Data Completeness
  const completenessStatus = summary.dataCompletenessScore >= 90 ? 'complete' : summary.dataCompletenessScore >= 75 ? 'warning' : 'pending';
  items.push({
    id: 'data_completeness',
    category: 'quality',
    title: 'Data Completeness Threshold',
    description: 'Data completeness score meets minimum requirements (90%)',
    status: completenessStatus,
    required: true,
    details: `Current completeness: ${summary.dataCompletenessScore}%`,
  });

  return items;
}

// Export report generation
export function generateReviewReport(
  summary: ReviewSummary,
  checklist: ChecklistItem[],
  projectName: string
): string {
  const lines: string[] = [];
  
  lines.push(`# Onboarding Review Report`);
  lines.push(`## Project: ${projectName}`);
  lines.push(`## Generated: ${new Date().toLocaleString()}`);
  lines.push('');
  
  // Summary Section
  lines.push('## Summary');
  lines.push(`- **Total Accounts:** ${summary.totalAccounts}`);
  lines.push(`- **Readiness Score:** ${summary.readinessScore}%`);
  lines.push(`- **Data Completeness:** ${summary.dataCompletenessScore}%`);
  lines.push('');
  
  // Configuration Breakdown
  lines.push('## Configuration Status');
  lines.push(`- **Collection Configured:** ${summary.accountsWithCollection}/${summary.totalAccounts} accounts`);
  lines.push(`  - Portal: ${summary.collectionSources.portal}`);
  lines.push(`  - Email: ${summary.collectionSources.email}`);
  lines.push(`  - Manual: ${summary.collectionSources.manual}`);
  lines.push(`  - API: ${summary.collectionSources.api}`);
  lines.push('');
  lines.push(`- **Parsing Configured:** ${summary.accountsWithParsing}/${summary.totalAccounts} accounts`);
  lines.push(`  - Vendor API: ${summary.parsingMethods.vendor_api}`);
  lines.push(`  - AI: ${summary.parsingMethods.ai}`);
  lines.push(`  - Manual: ${summary.parsingMethods.manual}`);
  lines.push('');
  lines.push(`- **GL Codes Assigned:** ${summary.accountsWithGLCode}/${summary.totalAccounts} accounts`);
  lines.push(`- **Payment Terms Set:** ${summary.accountsWithPayment}/${summary.totalAccounts} accounts`);
  lines.push('');
  
  // Checklist Section
  lines.push('## Pre-Go-Live Checklist');
  
  const categories = ['data', 'configuration', 'quality'];
  for (const category of categories) {
    const categoryItems = checklist.filter(i => i.category === category);
    lines.push(`\n### ${category.charAt(0).toUpperCase() + category.slice(1)}`);
    
    for (const item of categoryItems) {
      const statusIcon = item.status === 'complete' ? '✅' : item.status === 'warning' ? '⚠️' : '❌';
      const requiredTag = item.required ? '[Required]' : '[Optional]';
      lines.push(`${statusIcon} **${item.title}** ${requiredTag}`);
      lines.push(`   ${item.description}`);
      if (item.details) {
        lines.push(`   _${item.details}_`);
      }
    }
  }
  
  return lines.join('\n');
}

// CSV export
export function generateReviewCSV(
  summary: ReviewSummary,
  checklist: ChecklistItem[]
): string {
  const rows: string[][] = [];
  
  // Header
  rows.push(['Category', 'Item', 'Status', 'Required', 'Details', 'Count', 'Total']);
  
  // Data rows
  for (const item of checklist) {
    rows.push([
      item.category,
      item.title,
      item.status,
      item.required ? 'Yes' : 'No',
      item.details || '',
      item.count?.toString() || '',
      item.total?.toString() || '',
    ]);
  }
  
  // Summary row
  rows.push([]);
  rows.push(['Summary']);
  rows.push(['Metric', 'Value']);
  rows.push(['Total Accounts', summary.totalAccounts.toString()]);
  rows.push(['Readiness Score', `${summary.readinessScore}%`]);
  rows.push(['Data Completeness', `${summary.dataCompletenessScore}%`]);
  rows.push(['Accounts with Collection', summary.accountsWithCollection.toString()]);
  rows.push(['Accounts with Parsing', summary.accountsWithParsing.toString()]);
  rows.push(['Accounts with GL Code', summary.accountsWithGLCode.toString()]);
  
  return rows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
}
